import * as React from 'react'
import { useRouter } from 'next/router'

import { Stack, useTheme } from '@mui/material'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { styled } from '@mui/material/styles'

import { useCourseProgress, useLessonProgress, useResourceOverview } from 'components/hooks/state'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import { PathProgressLoading } from 'pages/path/progress'
import Text from 'components/atomic/atoms/text'
import Icon from 'components/atomic/atoms/icon'
import Button from 'components/atomic/atoms/button'
import { mapIcon, ResourceTypes } from 'utils/resources'
import Routes from 'utils/routes'

const Card = styled(Stack)`
    padding: 1rem;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`

interface ResumeContentProps {
    courseId: string
}

const ResumeContent = React.memo(({ courseId }: ResumeContentProps) => {
    const [timeToComplete, setTimeToComplete] = React.useState<number>()

    const { t }: I18n = useTranslation()
    const { palette } = useTheme()
    const router = useRouter()

    const { data: courseProgress } = useCourseProgress({ courseId })

    const { data: resource } = useResourceOverview({
        resourceId: courseProgress?.current_resource,
        shouldFetch: !!courseProgress?.current_resource,
    })

    const { data: lessonProgress } = useLessonProgress({
        lessonId: resource?._id,
        shouldFetch: resource?.type === ResourceTypes.LESSON,
    })

    const buttonCopy = React.useMemo(() => {
        return courseProgress?.resources.map((r) => r._id).includes(courseProgress?.current_resource)
            ? t('path:buttons.continueResource')
            : t('path:buttons.startResource')
    }, [courseProgress, t])

    React.useMemo(() => {
        if (resource) setTimeToComplete(Math.round(resource.time))
    }, [resource])

    const handleClick = () => {
        if (!resource) return

        if (resource?.type === ResourceTypes.LESSON) {
            router.push(
                Routes.Exercise('courses', courseId, resource?._id, lessonProgress?.current_exercise_number || 1),
            )
        } else {
            router.push(Routes.Resource('courses', courseId, resource?.type, resource?._id))
        }
    }

    if (!courseProgress || !resource) {
        return <PathProgressLoading />
    }

    return (
        <Stack spacing="1rem">
            <Text variant="h4">{t('path:pathProgressSection.startFrom')}</Text>
            <Card direction="row" justifyContent="space-between" alignItems="flex-end">
                <Stack spacing="1rem">
                    <Stack direction="row" spacing="0.5rem" alignItems="center">
                        <Icon color="primary" fontSize="small" name={mapIcon(resource.type)} />
                        <Text variant="overline" color="primary">
                            {t(`resources:types.${resource.type}`)}
                        </Text>
                    </Stack>

                    <Text variant="h5">{resource.title}</Text>

                    <Stack direction="row" spacing="0.5rem">
                        <Icon fontSize="small" name="time" sx={{ color: palette.text.secondary }} />
                        <Text variant="body2" color="text.secondary">
                            {t('path:pathProgressSection.remaining', { value: timeToComplete })}
                        </Text>
                    </Stack>
                </Stack>
                <Button variant="outlined" onClick={() => handleClick()} endIcon={<PlayCircleIcon />}>
                    {buttonCopy}
                </Button>
            </Card>
        </Stack>
    )
})

export default ResumeContent
